import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Award, Users, Clock, Globe2, BadgeCheck, Scale, FileBadge } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { LAWYERS } from "@/lib/lawyers";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema,
  buildLawyerPersonSchemas,
} from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  loader: async (): Promise<{ content: PageContent }> => {
    const { ABOUT_CONTENT } = await import("@/content/site-pages");
    return { content: ABOUT_CONTENT };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `เกี่ยวกับเรา — Thai Notary Law & Service ${config.nameEn}`;
    const description = `ทนาย Notary Public ขึ้นทะเบียนสภาทนายความฯ ใน${config.nameTh} — ${COMPANY.yearsExperience}+ ปี เอกสารกว่า ${COMPANY.documentsHandled.toLocaleString()} ฉบับ ทีมพูดได้ 6 ภาษา รู้จักทีม มาตรฐานและจริยธรรม`;
    const head = pageHeadForProvince({ title, description, path: "/about" });
    return {
      ...head,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildOrganizationSchema()),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "เกี่ยวกับเรา", url: `${site}/about` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": `${site}/about#aboutpage`,
            url: `${site}/about`,
            name: title,
            description,
            mainEntity: { "@id": `${site}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(loaderData?.content.faqs ?? [])),
        },
        ...buildLawyerPersonSchemas(LAWYERS).map((p) => ({
          type: "application/ld+json" as const,
          children: JSON.stringify(p),
        })),
      ],
    };
  },
});

function AboutPage() {
  const { content: longForm } = Route.useLoaderData();
  const { config, core: content } = getActiveProvince();

  const stats = [
    { icon: Award, label: `${COMPANY.yearsExperience}+ ปี`, sub: "ประสบการณ์" },
    { icon: FileBadge, label: `${COMPANY.documentsHandled.toLocaleString()}+`, sub: "เอกสารที่ดูแล" },
    { icon: Users, label: `${LAWYERS.length}`, sub: "ทนาย Notary" },
    { icon: Clock, label: COMPANY.responseTime, sub: "ตอบกลับ LINE" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-16 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <ShieldCheck className="h-3.5 w-3.5" /> About · {config.nameEn} Office
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              เกี่ยวกับ {COMPANY.legalName}
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-muted-foreground md:text-lg">
              ทนาย Notary Public ขึ้นทะเบียนสภาทนายความแห่งประเทศไทย ตามข้อบังคับ พ.ศ. 2551 —
              ให้บริการที่{config.nameTh}โดยทีมงานท้องถิ่นที่เข้าใจชุมชน Expat, นักลงทุน, Yacht crew และคู่รักไทย-ต่างชาติ
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 px-4 py-10 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.sub} className="rounded-xl border border-border bg-card p-5 text-center">
                <s.icon className="mx-auto mb-2 h-6 w-6 text-primary" />
                <div className="text-xl font-extrabold text-primary">{s.label}</div>
                <div className="text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Story */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-14">
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">เรื่องราวของเรา</h2>
            <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                {COMPANY.brandName} เริ่มต้นจากความตั้งใจของทีมทนายความและที่ปรึกษากฎหมายในจังหวัด{config.nameTh}
                ที่เห็นว่าชาวต่างชาติ นักลงทุน และคนไทยที่มีธุรกรรมระหว่างประเทศ
                ต้องเสียเวลาเดินทางไปกรุงเทพเพื่อทำเอกสาร Notary Public, แปลรับรอง และรับรองกงสุล
              </p>
              <p>
                เราจึงรวมทีมทนาย Notary Public ที่ขึ้นทะเบียนถูกต้องตามสภาทนายความฯ
                พร้อมนักแปลขึ้นทะเบียนกระทรวงยุติธรรม และ NAATI (ออสเตรเลีย) — ให้บริการในพื้นที่{config.nameTh}
                ครอบคลุมทุกอำเภอ ทุกตำบล รวมถึง on-site ที่วิลล่า โรงแรม มารีน่า และโรงพยาบาลนานาชาติ
              </p>
              <p>
                {content.localCharacter}
              </p>
            </div>
          </div>
        </section>

        {/* Why us local */}
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <h2 className="mb-6 text-2xl font-bold md:text-3xl">ทำไมต้องเลือกเรา ใน{config.nameTh}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {content.whyUsLocal.map((w) => (
                <div key={w.title} className="rounded-xl border border-border bg-card p-5">
                  <div className="mb-2 flex items-center gap-2">
                    <BadgeCheck className="h-5 w-5 text-gold" />
                    <h3 className="text-base font-bold">{w.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lawyers */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <h2 className="mb-2 text-2xl font-bold md:text-3xl">ทีมทนาย Notary Public</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              ทุกท่านขึ้นทะเบียนเป็น Notarial Services Attorney กับสภาทนายความฯ — มีบัตรประจำตัวและเลขใบอนุญาตตรวจสอบได้
            </p>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {LAWYERS.map((l) => (
                <article
                  key={l.id}
                  itemScope
                  itemType="https://schema.org/Person"
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={l.portrait}
                      alt={`${l.nameEn} — Notarial Services Attorney`}
                      width={72}
                      height={72}
                      loading="lazy"
                      decoding="async"
                      itemProp="image"
                      className="h-16 w-16 rounded-full object-cover ring-2 ring-gold/30"
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Scale className="h-4 w-4 text-primary" />
                        <span itemProp="name" className="text-base font-bold">{l.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground" itemProp="alternateName">{l.nameEn}</div>
                      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-gold" itemProp="jobTitle">
                        {l.title}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {l.specialties.map((s) => (
                      <span
                        key={s}
                        itemProp="knowsAbout"
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Languages / coverage */}
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">ภาษาที่ทีมรองรับใน{config.nameTh}</h2>
            <div className="flex flex-wrap gap-2">
              {["ไทย", "English", "Русский", "中文 (普通话)", "Deutsch", "Français", "Italiano", "日本語", "한국어", "العربية"].map((l) => (
                <span key={l} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold">
                  <Globe2 className="h-3 w-3 text-primary" /> {l}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">งานแปลรับรองรองรับมากกว่า 40 ภาษา สำหรับใช้ในไทยและทั่วโลก</p>
          </div>
        </section>

        <LongFormContent content={longForm} />

        {/* CTA */}
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center">
            <h2 className="text-2xl font-bold md:text-3xl">พร้อมเริ่มงานเอกสารของคุณใน{config.nameTh}</h2>
            <p className="mt-3 text-sm opacity-90">ปรึกษาฟรี ตอบกลับ {COMPANY.responseTime}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-3 text-sm font-bold text-gold-foreground">
                📞 {COMPANY.phone}
              </a>
              <Link to="/services" className="rounded-md border border-primary-foreground/40 px-5 py-3 text-sm font-bold">
                ดูบริการทั้งหมด
              </Link>
              <Link to="/contact" className="rounded-md border border-primary-foreground/40 px-5 py-3 text-sm font-bold">
                ติดต่อเรา
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}